// The user's LIVE view of a report — what the screen is actually showing, as
// opposed to InsightsContent.content, which is the saved definition. Sent to the
// Insights agent so it can answer "why is X low?" about the figures in front of
// the user instead of rediscovering a date range and filter set it was never
// told.
//
// Mirrored on the server by functions/src/insights/types.ts (guarded by
// functions/src/insights/__tests__/clientContract.test.ts). The prose the model
// actually reads is built server-side in functions/src/insights/buildViewContext.ts
// — this shape stays structured so the wording can iterate without a client
// release.

export interface InsightsViewFilterClause {
  field: string;
  kind: 'text' | 'number' | 'boolean';
  operator: string;
  values: (string | number | boolean)[];
}

export interface InsightsViewFilter {
  // Human-readable, from getDrillDownDescription (e.g. "Regions are West").
  description: string;
  // The agent-tool clauses this filter maps onto (AND-combined). One on-screen
  // filter can need more than one — e.g. "excluding Blanks, X, Y" becomes an
  // 'is not' clause plus an 'is not blank' clause. An EMPTY/absent list is the
  // signal that the agent cannot faithfully reproduce this filter (wildcards,
  // date drill-downs, "blank OR value").
  clauses?: InsightsViewFilterClause[];
  // Legacy singular mirror, set only when there's exactly one clause. Kept so a
  // newer client can still ground an older server that reads `clause`. Prefer
  // `clauses` / viewFilterClauses().
  clause?: InsightsViewFilterClause;
}

// The reproducible clauses of a view filter, tolerating the legacy singular
// field. Absence/empty ⇒ the agent can't reproduce this filter.
export const viewFilterClauses = (
  filter: Pick<InsightsViewFilter, 'clause' | 'clauses'>,
): InsightsViewFilterClause[] =>
  filter.clauses ?? (filter.clause ? [filter.clause] : []);

// One row of on-screen figures: what it's called, and a value per series/column.
export interface InsightsViewValueRow {
  label: string;
  values: { metric: string; value: number }[];
}

export interface InsightsViewValues {
  // When the numbers were captured, so the prose can say "as displayed at 14:03"
  // rather than implying they are live.
  asOf: string;
  // A gadget with no dimension and no trend (e.g. a smart card).
  total?: { metric: string; value: number };
  // A digest, never the raw rows — a daily trend over a year is 365 of them.
  rows?: InsightsViewValueRow[];
  rowsTruncatedFrom?: number;
}

export interface InsightsViewGadget {
  // Client-only join key: the canvas card's layout id, used to match this gadget
  // to its entry in the values registry. mergeValuesIntoView strips it before the
  // view goes over the wire — it means nothing to the model.
  cardId?: string;
  title: string;
  // Ids as well as names: sending the id saves the agent a find_metrics round
  // trip out of a budget of MAX_TOOL_ITERATIONS (8).
  metrics: { id: string; name?: string }[];
  dimensionA?: { field: string; label: string };
  dimensionB?: { field: string; label: string };
  // The trend bucket in business language (e.g. "month", "hour of day").
  trend?: string;
  // False when the trend/grouping has no aggregate_metric equivalent (fiscal
  // intervals, hour-of-day, day-of-week, fixed-day intervals).
  isTrendReproducible?: boolean;
  groupByLimit?: number;
  groupBySort?: string;
  // Only the DELTAS from the report-level scope — a gadget that inherits
  // everything carries none of these.
  dateFieldOverride?: string;
  extraFilters?: InsightsViewFilter[];
  ignoresReportFilters?: boolean;
  comparison?: string;
  values?: InsightsViewValues;
  // Why there are no values. Without this the model reads "no values" as
  // "no data" — an actively wrong inference.
  valuesUnavailableReason?: 'unsupported-gadget' | 'loading' | 'truncated';
  // A dashboard gadget's OWN resolved scope. Dashboards have no view-level date
  // range or filter set — each gadget carries its own — so this is ABSOLUTE, not
  // a delta from the view like dateFieldOverride/extraFilters/ignoresReportFilters
  // (which reports use). Filled at send time from the per-gadget scope registry
  // (see mergeScopeIntoView); reports never set it.
  ownScope?: InsightsGadgetScope;
  // Where a click on a value in this gadget navigates. Only dashboard gadgets
  // carry one (reportDrillDownId / boardDrillDownId); report gadgets never do —
  // on a report a click filters the whole page instead. Lets the agent read the
  // popup report's definition (get_content_definition) to pick the same
  // dimensions the user would see if they clicked.
  drillTarget?: InsightsViewDrillTarget;
  // Series that ask their own question (V5SeriesQueryOverrides): the same
  // metric with extra filters, an earlier period or another date field
  seriesOverrides?: InsightsViewSeriesOverride[];
}

export interface InsightsViewSeriesOverride {
  metric: { id: string; name?: string };
  // As the chart's legend shows it
  label: string;
  // The page's and gadget's filters don't apply, only its own (and its
  // metric's dataset filters)
  ignoresFilters?: boolean;
  // On top of the gadget's own, or instead of them when ignoresFilters
  filters?: InsightsViewFilter[];
  // e.g. "the same period 2 years earlier"
  period?: string;
  dateField?: string;
}

export interface InsightsViewDrillTarget {
  type: 'report' | 'board';
  id: string;
  name?: string;
}

// The single on-screen value the user asked the agent to explain ("Explain this
// value"). Built client-side from the clicked point/cell (see
// components/InsightsChat/explainValue), pinned with the rendered figures at
// send time (mergeFocusIntoView) and rendered server-side as the FOCUS block.
export interface InsightsViewFocus {
  // Client-only: the entity the focus was captured on. mergeFocusIntoView drops
  // the whole focus when it doesn't match the entity being sent, so a value
  // picked on one dashboard is never attached to another's view.
  entityId?: string;
  // Client-only join key into the values registry (stripped before the wire).
  cardId?: string;
  // Client-only: the clicked term exactly as the chart reported it — for a
  // trend, the raw response date the digest files its row under — so the
  // focused row is found through sampling (stripped before the wire).
  term?: string;
  // Matches the gadget's `title` so the server can pin that gadget's block.
  gadgetTitle: string;
  metrics: { id: string; name?: string }[];
  dimension?: { field: string; label: string };
  // The clicked member. `clause` is the tool-ready filter; absent when the click
  // has no metric-query equivalent (wildcard/blank/hour/day-of-week groupings).
  value?: {
    label: string;
    rawValue: string | number | boolean;
    clause?: InsightsViewFilter['clause'];
  };
  // Set when the clicked value is a trend bucket (or the row is a date bucket).
  dateBucket?: { startDate: string; endDate: string; label?: string };
  // The figure(s) rendered for that value, one per metric in the gadget. Filled
  // at send time from the focused card's digest.
  onScreenValues?: { metric: string; value: number }[];
  drillTarget?: InsightsViewDrillTarget;
}

// A dashboard gadget's absolute, already-resolved scope (concrete ISO dates +
// humanised/claused filters), captured deep in the tree because a dashboard has
// no shared scope to derive it from at the top.
export interface InsightsGadgetScope {
  startDate?: string;
  endDate?: string;
  dateField?: string;
  weekStartsOn?: string;
  comparison?: string;
  filters?: InsightsViewFilter[];
}

export interface InsightsView {
  // Always concrete ISO dates — a relative label like "Last month" would leave
  // the agent guessing a boundary.
  startDate?: string;
  endDate?: string;
  dateField?: string;
  comparison?: string;
  filters: InsightsViewFilter[];
  filtersTruncatedFrom?: number;
  // True when ANY applied filter or grouping cannot be expressed via
  // aggregate_metric — including the dataset default filters the report applies
  // invisibly. Drives the prose's "your figure may legitimately differ" clause.
  hasUnreproducibleFilters: boolean;
  gadgets: InsightsViewGadget[];
  gadgetsTruncatedFrom?: number;
  weekStartsOn?: string;
  // Present when the user pointed at ONE specific value ("Explain this value").
  focus?: InsightsViewFocus;
}

// What a dashboard gadget's scope getter returns (see registerGadgetScope). The
// resolved scope plus whether any of its filters/groupings can't be reproduced by
// aggregate_metric — the latter is OR-ed into the view's hasUnreproducibleFilters
// at send time, since a dashboard's view-level filters are always empty.
export interface InsightsGadgetScopeFragment {
  ownScope: InsightsGadgetScope;
  hasUnreproducibleFilters: boolean;
}

// What a gadget's value getter returns. Kept as a union so a gadget that is
// mid-refetch reports that fact rather than handing back the previous filters'
// numbers.
export type InsightsGadgetValues =
  | InsightsViewValues
  | { isLoading: true }
  | undefined;

// Which data hook a card's figures came from. Gadget data does not flow through
// one hook: chart-shaped gadgets (bar, pie, timeSeries, smartCard, …) resolve via
// useV5ChartData, while table- and stack-shaped ones (matrix, stackedBar,
// stackedArea, heatmap, sankey) resolve via useMetricMatrix. A few gadgets touch
// both, so registrations are kept apart per source and reconciled on read —
// otherwise whichever hook ran last would silently clobber the other.
export type InsightsValuesSource = 'v5' | 'matrix';

// Preferred first. 'v5' is the series the chart itself plots, so where a gadget
// has both it is the more faithful record of what's on screen.
export const INSIGHTS_VALUES_SOURCE_PRIORITY: InsightsValuesSource[] = [
  'v5',
  'matrix',
];
